import { createSelector } from 'reselect';
import { getWorkOrderManagement } from './getWorkOrderManagement';
import { getPostLaborUpdatesOnNats } from './getPostLaborUpdatesOnNats';
import { getIsApmERPJobFlow } from './getIsApmERPJobFlow';
import {
  getIsOperatorSignedIn,
  getSignedInOperatorErpId,
} from './getSignedInOperator';

const isOpenTicket = (ticket) => {
  return ticket?.state !== 'CLOSED' && ticket?.clockOut == null;
};

export const getRawCurrentLaborTicket = createSelector(
  [
    getWorkOrderManagement,
    getPostLaborUpdatesOnNats,
    getIsApmERPJobFlow,
    getIsOperatorSignedIn,
    getSignedInOperatorErpId,
  ],
  (
    workOrderManagement,
    natsFlowEnabled,
    isApmERPJobFlow,
    isSignedIn,
    erpId
  ) => {
    // Split raw vs filtered access because synthetic selectors still need CLOSED ticket data.
    const ticket = workOrderManagement?.currentLaborTicket;

    // NATS labor flow: the stored ticket follows the same ownership rule as the
    // ticket query (getLaborTicketWhereForMachine), so a sign-in, sign-out, or
    // operator switch takes effect at once rather than when the next query
    // result arrives: an open ticket counts only for the signed-in operator.
    // Closed tickets (e.g. keep-open) are unaffected. APM supplies its own ticket.
    if (
      ticket &&
      natsFlowEnabled &&
      !isApmERPJobFlow &&
      isOpenTicket(ticket) &&
      !(
        isSignedIn &&
        erpId != null &&
        String(ticket.personId) === String(erpId)
      )
    ) {
      return null;
    }
    return ticket;
  }
);

/**
 * Gets the current labor ticket for the active machine.
 * This is scoped to the current machine via the subscription filter in useRealTimeData.
 * For clock-out flow across all machines, use getClockOutOpenLaborTicketCount instead.
 */
export const getCurrentLaborTicket = createSelector(
  [getRawCurrentLaborTicket, getPostLaborUpdatesOnNats],
  (currentLaborTicket, postLaborUpdatesOnNats) => {
    if (currentLaborTicket?.state === 'CLOSED') {
      const endBehavior = currentLaborTicket?.customFields?.endBehavior;
      // keep-open only applies in the NATS labor-updates flow (not MES / standard ERP)
      // Hide other CLOSED tickets here instead of in Redux so raw consumers can still read the subscription payload.
      if (!(postLaborUpdatesOnNats && endBehavior === 'keep-open')) {
        return null;
      }
    }

    return currentLaborTicket;
  }
);
